import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import SubMenuItem from './SubMenuItem';
import { MODAL_SKLO } from '../../lib/uiTokens';
import { barvaSekce, modulStranky } from '../../lib/moduly';

/**
 * SubMenu — vyskakovací panel sekce vedle lišty.
 *
 * Skleněný panel ve stejném materiálu jako modály (MODAL_SKLO), 8 px od lišty.
 * Hlavička = název sekce v její barvě a jednořádkový popis, pod ní moduly.
 * Hlavička sedí na výšce ikony sekce; když by panel přetekl spodek okna,
 * posune se nahoru (měří se skutečná výška).
 *
 * @param {Object} props
 * @param {Object} props.section - Sekce s moduly, které uživatel vidí
 * @param {string} props.currentPage
 * @param {(itemId: string) => number} [props.getBadge]
 * @param {number} [props.anchorTop] - Y ikony sekce ve viewportu
 * @param {() => void} props.onClose
 * @param {(pageId: string) => void} props.onNavigate
 * @param {() => void} [props.onMouseEnter]
 * @param {() => void} [props.onMouseLeave]
 * @param {boolean} props.isDark
 */
export default function SubMenu({
    section,
    currentPage,
    getBadge = () => 0,
    anchorTop = 0,
    onClose,
    onNavigate,
    onMouseEnter,
    onMouseLeave,
    isDark = true,
}) {
    const panelRef = useRef(null);
    const [top, setTop] = useState(anchorTop);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    useLayoutEffect(() => {
        const vyska = panelRef.current?.offsetHeight || 0;
        setTop(Math.max(8, Math.min(anchorTop, window.innerHeight - vyska - 8)));
    }, [anchorTop, section?.id]);

    if (!section) return null;

    const aktivniModul = modulStranky(currentPage)?.item.id;
    const hranice = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)';

    return (
        <aside
            ref={panelRef}
            className="sidebar-submenu"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{
                position: 'fixed',
                top,
                left: 68,
                width: 236,
                maxHeight: 'calc(100vh - 16px)',
                zIndex: 50,
                ...MODAL_SKLO,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                animation: 'sidebar-submenu-in 0.14s ease-out',
            }}
        >
            <style>{`
                @keyframes sidebar-submenu-in {
                    from { transform: translateX(-6px); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
            `}</style>

            <header style={{ padding: '10px 14px 9px', borderBottom: `1px solid ${hranice}`, flexShrink: 0 }}>
                {/* Tón podle motivu — sytá barva sekce je na světlém panelu
                    pod hranicí čitelnosti (2,1–3,5 : 1). */}
                <div style={{ fontSize: 13.5, fontWeight: 700, color: barvaSekce(section, isDark), lineHeight: 1.25 }}>
                    {section.label}
                </div>
                {section.popis && (
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 1, lineHeight: 1.3 }}>
                        {section.popis}
                    </div>
                )}
            </header>

            <nav style={{ flex: 1, overflowY: 'auto', padding: 6, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {section.items.map((item) => (
                    <SubMenuItem
                        key={item.id}
                        item={item}
                        active={aktivniModul === item.id}
                        accentColor={section.color}
                        badge={getBadge(item.id)}
                        isDark={isDark}
                        onClick={() => onNavigate(item.id)}
                    />
                ))}
            </nav>
        </aside>
    );
}
